import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Button, Card, Form } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import Modal from "react-bootstrap/Modal";
import { ApexOptions } from "apexcharts";
import moment from "moment";
import useSensorManagement from "../../Hooks/useSensor";
import Loading from "../LoadingPage/Loading";

interface IGraphModel {
  showModal: boolean;
  toggleModal: () => void;
  modelName: string;
  sensorData: {
    id: string;
    inCentigrade?: boolean;
  } | null;
}

interface GraphData {
  timeStamp: string[];
  temp: number[];
  alerts?: boolean[];
  tempHigh: number;
  tempLow: number;
}

interface TemperatureLog {
  timeStamp: string;
  temp: number;
  alert: boolean;
}

interface SensorLogsResponse {
  temperatureLogs: TemperatureLog[];
  higherTempLimit: number;
  lowerTempLimit: number;
}

const FREQUENCY_OPTIONS = [
  { value: 1, label: "Last 24 Hours" },
  { value: 7, label: "Last 7 Days" },
  { value: 30, label: "Last 30 Days" },
] as const;

const initialGraphData = {
  timeStamp: [],
  temp: [],
  tempHigh: 0,
  tempLow: 0,
};

const GraphModel: React.FC<IGraphModel> = ({
  showModal,
  toggleModal,
  modelName,
  sensorData,
}) => {
  const [graphData, setGraphData] = useState<GraphData>(initialGraphData);
  const [selectedFrequency, setSelectedFrequency] = useState<number>(1);
  const { getSensorLogsAPI, getSensorLogsLoading } = useSensorManagement();

  useEffect(() => {
    if (showModal && sensorData?.id) {
      fetchGraphData(selectedFrequency);
    }
  }, [showModal, sensorData?.id, selectedFrequency]);

  const fetchGraphData = useCallback(
    async (frequency: number = 1) => {
      if (!sensorData?.id) return;

      try {
        const response: SensorLogsResponse = await getSensorLogsAPI({
          id: sensorData.id,
          ...(frequency > 1
            ? { frequency }
            : {}) /* Add frequency params if selected value is more then 1 day */,
        });

        if (response?.temperatureLogs) {
          const { temperatureLogs, higherTempLimit, lowerTempLimit } = response;

          setGraphData({
            timeStamp: temperatureLogs.map((log) => log.timeStamp),
            temp: temperatureLogs.map((log) => log.temp),
            alerts: temperatureLogs.map((log) => log.alert),
            tempHigh: higherTempLimit || 0,
            tempLow: lowerTempLimit || 0,
          });
        }
      } catch (error) {
        console.error("Error fetching sensor logs:", error);
        setGraphData(initialGraphData);
      }
    },
    [getSensorLogsAPI, sensorData?.id]
  );

  const temperatureUnit = useMemo(
    () => (sensorData?.inCentigrade ? "°C" : "°F"),
    [sensorData?.inCentigrade]
  );

  const chartOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        id: "temperature-chart",
        type: "line",
        zoom: { enabled: true },
        toolbar: {
          show: true,
          offsetY: -20,
          offsetX: -16,
          export: {
            csv: {
              headerCategory: "Date / Time",
              categoryFormatter(x: any) {
                return moment(x).format("MM/DD/YYYY hh:mm:ss A");
              },
            },
          },
          tools: {
            customIcons: [
              ...FREQUENCY_OPTIONS.map((item) => ({
                icon: `${item.value}D`,
                title: item.label,
                class: `frequency-selection ${selectedFrequency === item.value ? "selected" : ""}`,
                click: () => setSelectedFrequency(item.value),
              })),
              {
                icon: '<svg width="2" height="20"><line x1="1" y1="0" x2="1" y2="20" stroke="#ccc" stroke-width="2"/></svg>',
                title: "divider",
                class: "icon-devider",
                click: () => {},
              },
            ],
          },
        },
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
        },
      },
      stroke: {
        curve: "smooth",
        width: 2,
      },
      markers: {
        size: 0,
        hover: { size: 6 },
      },
      xaxis: {
        type: "datetime",
        labels: {
          datetimeUTC: false,
          format: "MM/dd HH:mm",
        },
        title: {
          text: "Date / Time",
          offsetY: 8,
          style: { fontSize: "14px", fontWeight: 600 },
        },
      },
      yaxis: {
        title: {
          text: `Temperature, ${temperatureUnit}`,
          offsetX: -5,
          style: { fontSize: "14px", fontWeight: 600 },
        },
        max: graphData.tempHigh ? graphData.tempHigh + 5 : undefined,
        min: graphData.tempLow ? graphData.tempLow - 5 : undefined,
        labels: {
          formatter: (value: number) => `${value.toFixed(1)}`,
        },
      },
      annotations: {
        yaxis:
          graphData.tempHigh && graphData.tempLow
            ? [
                {
                  y: graphData.tempHigh,
                  borderColor: "#dc3545",
                  strokeDashArray: 0,
                  borderWidth: 2,
                  label: {
                    style: {
                      color: "#dc3545",
                      fontSize: "12px",
                      fontWeight: "bold",
                    },
                    text: `Max Limit (${graphData.tempHigh}${temperatureUnit})`,
                  },
                },
                {
                  y: graphData.tempLow,
                  borderColor: "#4444FF",
                  strokeDashArray: 0,
                  borderWidth: 2,
                  label: {
                    style: {
                      color: "#4444FF",
                      fontSize: "12px",
                      fontWeight: "bold",
                    },
                    text: `Min Limit (${graphData.tempLow}${temperatureUnit})`,
                  },
                },
              ]
            : [],
      },
      legend: {
        position: "bottom",
        horizontalAlign: "center",
        offsetY: 10,
        fontSize: "14px",
      },
      tooltip: {
        x: {
          format: "MM/dd/yy HH:mm:ss",
          show: false,
        },
        y: {
          formatter: (value: number) =>
            `${value?.toFixed(2)}${temperatureUnit}`,
        },
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
          const value = series[seriesIndex][dataPointIndex];
          if (!value) return "";
          return `<div class="apex-tooltip-custom ${graphData.alerts?.[dataPointIndex]}">${value?.toFixed(2)}${temperatureUnit}</div>`;
        },
      },
      grid: {
        borderColor: "#e0e0e0",
        strokeDashArray: 3,
      },
      colors: ["#2E93fA"],
    }),
    [graphData, selectedFrequency, temperatureUnit]
  );

  const chartSeries = useMemo(() => {
    if (graphData.timeStamp.length > 0) {
      return [
        {
          name: "Temperature",
          data: graphData.timeStamp.map((ts, i) => ({
            x: new Date(ts).getTime(),
            y: graphData.temp[i] ?? null,
          })),
        },
      ];
    }

    /* Dummy series to force toolbar visibility */
    return [
      {
        name: "Temperature",
        data: [{ x: new Date().getTime(), y: null }],
      },
    ];
  }, [graphData]);

  if (!showModal) return null;

  return (
    <Modal
      show={showModal}
      onHide={toggleModal}
      centered
      size="lg"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>{modelName}</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: "73vh", overflowY: "auto" }}>
        {getSensorLogsLoading && <Loading />}

        {/* <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="flex-grow-1" />
          <Form.Control
            as="select"
            value={selectedFrequency}
            onChange={handleFrequencyChange}
            style={{ maxWidth: "200px" }}
            disabled={getSensorLogsLoading}
            aria-label="Select time range"
          >
            {FREQUENCY_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Control>
        </div> */}
        <div className="w-100 pt-4 d-flex justify-content-center">
          <ReactApexChart
            options={chartOptions}
            series={chartSeries}
            type="line"
            height={400}
            width={760}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default GraphModel;
